import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

export const BulkImportPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/products/csv-template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/products/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} products!`);
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      
      setFile(null);
      document.getElementById('file-input').value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      setResult(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-barlow text-4xl font-bold text-slate-900 mb-2" data-testid="bulk-import-title">
            Bulk Product Import
          </h1>
          <p className="text-slate-600">Upload a CSV file to import multiple products at once</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-6">
          <div className="mb-6">
            <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-3">Step 1: Download Template</h3>
            <p className="text-slate-600 text-sm mb-4">
              Download the CSV template with sample data and required column format
            </p>
            <Button
              onClick={handleDownloadTemplate}
              variant="outline"
              className="gap-2"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4" />
              Download CSV Template
            </Button>
          </div>

          <div className="border-t border-slate-200 pt-6 mb-6">
            <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-3">Step 2: Prepare Your Data</h3>
            <ul className="text-sm text-slate-600 space-y-2 list-disc list-inside">
              <li>Fill in the CSV template with your product data</li>
              <li>Ensure all required columns are filled: name, category, description, cost_price, recommended_selling_price, moq, delivery_timeline, colors_variants, stock_status</li>
              <li>For branding_options, use comma-separated values: logo_print, embroidery, engraving, etc.</li>
              <li>For tags, use comma-separated values: premium, tech, festive</li>
              <li>Stock status must be one of: In Stock, Available on Order, Limited Stock, Discontinued</li>
              <li>Products will be created with placeholder images (you can update them later)</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-3">Step 3: Upload CSV File</h3>
            <div className="mb-4">
              <label
                htmlFor="file-input"
                className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-consortium-green transition-colors bg-slate-50"
                data-testid="file-upload-area"
              >
                <div className="text-center">
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 font-medium">
                    {file ? file.name : 'Click to select CSV file'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">CSV files only</p>
                </div>
                <input
                  id="file-input"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full bg-consortium-green hover:bg-consortium-green-hover gap-2"
              data-testid="upload-csv-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {uploading ? 'Importing...' : 'Import Products'}
            </Button>
          </div>
        </div>

        {result && (
          <div className="space-y-4">
            <Alert className={result.imported_count > 0 ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}>
              {result.imported_count > 0 ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-orange-600" />
              )}
              <AlertTitle className="font-semibold">
                {result.imported_count > 0 ? 'Import Successful!' : 'Import Completed with Errors'}
              </AlertTitle>
              <AlertDescription>
                Imported {result.imported_count} out of {result.total_rows} products
                {result.errors && result.errors.length > 0 && (
                  <span> ({result.errors.length} errors)</span>
                )}
              </AlertDescription>
            </Alert>

            {result.errors && result.errors.length > 0 && (
              <div className="bg-white rounded-xl border border-red-200 p-6">
                <h4 className="font-semibold text-red-900 mb-3">Import Errors:</h4>
                <ul className="text-sm text-red-700 space-y-1 max-h-60 overflow-y-auto">
                  {result.errors.map((error, idx) => (
                    <li key={idx} className="font-mono text-xs">{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Image Upload via Google Drive
          </h4>
          <div className="text-sm text-blue-800 space-y-3">
            <p className="font-medium">To add product images, use Google Drive URLs in the CSV:</p>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>Upload product images to Google Drive</li>
              <li>Right-click image → Get link → Change to &quot;Anyone with the link&quot;</li>
              <li>Copy the share link (e.g., https://drive.google.com/file/d/FILE_ID/view)</li>
              <li>Paste link in the <code className="bg-blue-100 px-1 rounded">image_urls</code> column</li>
              <li>For multiple images, separate URLs with commas</li>
            </ol>
            <p className="text-xs mt-3 bg-blue-100 p-2 rounded">
              💡 <strong>Supported formats:</strong> Google Drive share links, direct image URLs (.jpg, .png, .webp)
            </p>
          </div>
        </div>

        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-6">
          <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Important Notes
          </h4>
          <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
            <li>Google Drive links will be automatically converted to download URLs</li>
            <li>Make sure images are publicly accessible (Anyone with the link)</li>
            <li>If image download fails, placeholder image will be used</li>
            <li>Invalid rows will be skipped, and valid rows will be imported</li>
            <li>Duplicate products (by name) will create separate entries</li>
            <li>Cost price and internal notes will only be visible to admin users</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
